import React, { useEffect, useState } from "react";
import { Button, Input, DatePicker, Table, message, Select, Space, Drawer, Form, InputNumber } from "antd";
import { SearchOutlined, RedoOutlined, DollarOutlined, CheckOutlined } from "@ant-design/icons";
import API from "../../api";
import dayjs from "dayjs";
import CustomTable from "../../components/Table/CustomTable";

const { Option } = Select;
const { RangePicker } = DatePicker;

export default function DamagedProducts() {
    const [damagedProducts, setDamagedProducts] = useState([]);
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationInfo, setPaginationInfo] = useState({ current: 1, pageSize: 20, total: 0 });
    const [filters, setFilters] = useState({
        fromDate: null,
        toDate: null,
        productName: "",
        invoiceId: "",
        replacementInvoiceId: "",
        storeName: null,
    });
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedDamageProduct, setSelectedDamageProduct] = useState(null);

    const fetchStores = async () => {
        try {
            const response = await API.get("/stores");
            setStores(response?.data || []);
        } catch (error) {
            console.error("Error fetching stores:", error);
            message.error("Failed to fetch stores.");
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value || null }));
    };

    const fetchDamagedProducts = async (page = 1, pageSize = 20) => {
        try {
            setLoading(true);
            const params = {
                page,
                limit: pageSize,
                fromDate: filters.fromDate ? dayjs(filters.fromDate).format("YYYY-MM-DD") : null,
                toDate: filters.toDate ? dayjs(filters.toDate).format("YYYY-MM-DD") : null,
                productName: filters.productName || null,
                invoiceId: filters.invoiceId || null,
                replacementInvoiceId: filters.replacementInvoiceId || null,
                storeName: filters.storeName || null,
            };

            const response = await API.get("/damaged-products", { params });

            setDamagedProducts(response?.data?.data || []);
            setPaginationInfo({
                current: page,
                pageSize,
                total: response?.data?.totalPages * pageSize,
            });
        } catch (err) {
            console.error("Error fetching damaged products:", err);
            message.error("Failed to load damaged products.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStores();
        fetchDamagedProducts();
        const store = JSON.parse(localStorage.getItem('userData'));
        const data = stores.find((s) => s._id == store?.storeId);
        handleFilterChange('storeName', data?.name);
    }, []);

    useEffect(() => {
        fetchDamagedProducts(paginationInfo.current, paginationInfo.pageSize);
    }, [filters]);

    const handleReplace = async (record) => {
        try {
            await API.post(`/damaged-products/replace-stock/${encodeURIComponent(record.invoiceId)}`);
            message.success("Product stock replaced successfully");
            fetchDamagedProducts(paginationInfo.current, paginationInfo.pageSize);
        } catch (error) {
            console.error("Error replacing product stock:", error);
            message.error("Failed to replace product stock.");
        }
    };
    const handleCompensate = (record) => {
        setSelectedDamageProduct(record);
        setDrawerVisible(true);
    };

    const onFinish = async (values) => {
        try {
            await API.post(`/damaged-products/compansate/${encodeURIComponent(selectedDamageProduct.invoiceId)}`, {
                amount: values.amount,
                paymentMethod: values.paymentMethod,
                compensationDate: values.compensationDate.format("YYYY-MM-DD"),
            });
            message.success("Compensation recorded successfully");
            setDrawerVisible(false);
            setSelectedDamageProduct(null);
            fetchDamagedProducts(paginationInfo.current, paginationInfo.pageSize);
        } catch (error) {
            console.error("Error compensating damage product:", error);
            message.error("Failed to record compensation.");
        }
    };

    const columns = [
        {
            title: "#",
            key: "index",
            render: (_, __, index) => (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
        },
        {
            title: "Product Name",
            dataIndex: ["productId", "specifications", "name"],
            key: "productName",
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search Product"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => {
                            handleFilterChange("productName", selectedKeys[0] || "");
                            confirm();
                        }}
                        style={{ marginBottom: 8, display: "block" }}
                    />
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={() => {
                            handleFilterChange("productName", selectedKeys[0] || "");
                            confirm();
                        }}
                        style={{ width: "100%" }}
                    >
                        Search
                    </Button>
                </div>
            ),
            filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        },
        {
            title: "Invoice ID",
            dataIndex: "invoiceId",
            key: "invoiceId",
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search Invoice ID"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => {
                            handleFilterChange("invoiceId", selectedKeys[0] || "");
                            confirm();
                        }}
                        style={{ marginBottom: 8, display: "block" }}
                    />
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={() => {
                            handleFilterChange("invoiceId", selectedKeys[0] || "");
                            confirm();
                        }}
                        style={{ width: "100%" }}
                    >
                        Search
                    </Button>
                </div>
            ),
            filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        },
        {
            title: "Replacement Invoice ID",
            dataIndex: "replacementInvoiceId",
            key: "replacementInvoiceId",
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search Replacement Invoice ID"
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => {
                            handleFilterChange("replacementInvoiceId", selectedKeys[0] || "");
                            confirm();
                        }}
                        style={{ marginBottom: 8, display: "block" }}
                    />
                    <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={() => {
                            handleFilterChange("replacementInvoiceId", selectedKeys[0] || "");
                            confirm();
                        }}
                        style={{ width: "100%" }}
                    >
                        Search
                    </Button>
                </div>
            ),
            filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        },
        {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => status || "pending",
        },
        {
            title: "Date",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => dayjs(date).format("YYYY-MM-DD"),
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) => {
                if (record.status === "pending") {
                    return (
                        <Space>
                            <Button icon={<RedoOutlined />} onClick={() => handleReplace(record)}>
                                Replace
                            </Button>
                            <Button icon={<DollarOutlined />} onClick={() => handleCompensate(record)}>
                                Compensate
                            </Button>
                        </Space>
                    );
                }
                return null;
            },
        },
    ];

    return (
        <div style={{
            padding: "20px",
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}>
            <h2>Damaged Products</h2>

            <div style={{ marginBottom: 16, display: "flex", gap: 8 }}>
                <RangePicker
                    onChange={(dates) => {
                        handleFilterChange("fromDate", dates ? dates[0] : null);
                        handleFilterChange("toDate", dates ? dates[1] : null);
                    }}
                    format="YYYY-MM-DD"
                />
                <Select
                    placeholder="Select Store"
                    value={filters.storeName}
                    onChange={(value) => handleFilterChange("storeName", value)}
                    style={{ width: 200 }}
                    allowClear
                    loading={loading}
                >
                    {stores.map((store) => (
                        <Option key={store._id} value={store.name}>
                            {store.name}
                        </Option>
                    ))}
                </Select>
                <Button type="primary" icon={<SearchOutlined />} onClick={() => fetchDamagedProducts(1, paginationInfo.pageSize)}>
                    Apply Filters
                </Button>
            </div>

            <CustomTable
                columns={columns}
                dataSource={damagedProducts}
                rowKey="_id"
                loading={loading}
                pagination={{
                    current: paginationInfo.current,
                    pageSize: paginationInfo.pageSize,
                    total: paginationInfo.total,
                    onChange: (page, pageSize) => fetchDamagedProducts(page, pageSize),
                }}
            />

            <Drawer
                title="Compensate Damaged Product"
                visible={drawerVisible}
                onClose={() => {
                    setDrawerVisible(false);
                    setSelectedDamageProduct(null);
                }}
                width={400}
            >
                <Form layout="vertical" onFinish={onFinish} initialValues={{ paymentMethod: "credit_card" }}>
                    <Form.Item
                        name="amount"
                        label="Compensation Amount"
                        rules={[{ required: true, message: "Please enter the compensation amount" }]}
                    >
                        <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>

                    <Form.Item
                        name="paymentMethod"
                        label="Payment Method"
                        rules={[{ required: true, message: "Please select a payment method" }]}
                    >
                        <Select>
                            <Option value="credit_card">Credit Card</Option>
                            <Option value="cash">Cash</Option>
                            <Option value="bank_transfer">Bank Transfer</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="compensationDate"
                        label="Compensation Date"
                        rules={[{ required: true, message: "Please select the compensation date" }]}
                    >
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" icon={<CheckOutlined />} htmlType="submit" loading={loading}>
                            Submit Compensation
                        </Button>
                    </Form.Item>
                </Form>
            </Drawer>
        </div>
    );
}